export default {
  title: 'Create Express Order',
  steps: {
    basic: 'Basic Information',
    metrics: 'Package Metrics',
    exclusive: 'Carrier-specific Information'
  },
  common: {
    clear: 'Clear',
    addFile: 'Add File',
    select: 'Select',
    required: 'Required',
    optional: 'Optional',
    cancel: 'Cancel',
    close: 'Close',
    saveDraft: 'Save Draft',
    previousStep: 'Previous',
    nextStep: 'Next',
    confirmOrder: 'Place Order',
    chooseFile: 'Choose File',
    remove: 'Remove',
    storedInFiles: 'Stored in File Management',
    reupload: 'Upload Again',
    no: 'No',
    none: 'None',
    yes: 'Yes',
    pending: 'Pending',
    systemConfigured: 'System Configured'
  },
  sections: {
    productDetails: 'Product Details',
    expressService: 'Express Service',
    cargoInfo: 'Logistics Information',
    metrics: 'Package Metrics',
    pieceDetails: 'Piece Details',
    contacts: 'Contact Address Information',
    fedex: 'FedEx-specific Information',
    dhl: 'DHL-specific Information',
    importer: 'Importer Information',
    importerOptional: 'Importer Information (Optional)',
    hsCodes: 'Product HS Codes',
    hsCodeHint: '(one row per product; export/import default to the same HS code and can be edited separately)'
  },
  fields: {
    shipDocumentType: 'Document Type',
    carrier: 'Express Carrier',
    product: 'Express Product',
    account: 'Shipping Account',
    pickup: 'Pickup',
    pickupTime: 'Pickup Time',
    dutyPayment: 'Duties and Taxes Payer',
    dutyAccount: 'Duties and Taxes Account',
    englishName: 'English Product Name',
    chineseName: 'Chinese Product Name',
    originCountry: 'Country of Origin',
    transportRemark: 'Transport Remark',
    incoterm: 'Incoterm',
    invoiceValue: 'Goods Value',
    currency: 'Currency',
    shipperPartyType: 'Shipper Address Type',
    recipientPartyType: 'Recipient Address Type',
    shipperCountry: 'Shipper Country',
    recipientCountry: 'Recipient Country',
    shipperPostcode: 'Shipper Postal Code',
    recipientPostcode: 'Recipient Postal Code',
    packagingType: 'Packaging Type',
    paymentType: 'Payment Type',
    batteryDetails: 'Battery Type and Packing',
    shipmentPurpose: 'Shipment Purpose',
    recipientTaxId: 'Recipient Tax ID',
    bookingNumber: 'Booking Number',
    loadCount: 'Package Count',
    fileUpload: 'Upload File',
    fileUploadEnabled: 'Upload required (true uses the eTower file upload API)',
    uploadFile: 'File',
    brokerSelection: 'Broker Selection',
    brokerSelectionEnabled: 'Select a broker (complete the broker details below)',
    brokerName: 'Broker Name',
    brokerPhone: 'Broker Phone',
    brokerCountry: 'Broker Country',
    brokerState: 'Broker State/Province',
    brokerCity: 'Broker City',
    brokerPostcode: 'Broker Postal Code',
    brokerAddress: 'Broker Address',
    invoice: 'Invoice',
    invoiceOption: 'Invoice Options',
    productType: 'Product Type',
    invoiceType: 'Invoice Type',
    exportType: 'Export Type',
    dangerousGoods: 'Dangerous Goods',
    invoiceDate: 'Invoice Date',
    invoiceNumber: 'Invoice Number',
    invoiceAttachment: 'Invoice File',
    invoiceAttachmentFormat: 'Invoice Attachment Format',
    importerName: 'Importer Contact Name',
    importerCompany: 'Importer Company Name',
    importerPhone: 'Importer Phone',
    importerCountry: 'Importer Country/Region',
    importerPostcode: 'Importer Postal Code',
    importerCity: 'Importer City',
    importerAddress1: 'Importer Address 1',
    importerAddress2: 'Importer Address 2',
    importerAddress3: 'Importer Address 3',
    masterSubOrder: 'Parent-child Shipment (Multi-piece)',
    subOrderCount: 'Piece Count'
  },
  contactEnglish: {
    parties: {
      shipper: 'shipper',
      recipient: 'recipient'
    },
    fields: {
      company: 'Company',
      postcode: 'Postcode',
      contactName: 'contact person',
      phone: 'phone number',
      province: 'province/state',
      city: 'city',
      district: 'district/county',
      address: 'detailed address'
    }
  },
  placeholders: {
    selectCarrierFirst: 'Select a carrier first',
    selectAccount: 'Select an account',
    selectPickupTime: 'Select pickup time',
    selectDutyPayment: 'Select duties and taxes payer',
    dutyAccount: 'Enter or select a previously used duty account',
    optionalDutyAccount: 'Optional: enter or select a previously used duty account',
    selectShipperPartyType: 'Select shipper address type',
    selectRecipientPartyType: 'Select recipient address type',
    englishName: 'From the consignment; editable',
    optionalEnglishName: 'Optional; from the consignment; editable',
    transportRemark: 'Optional; populated from the consignment logistics information',
    selectShipperCountry: 'Select shipper country',
    selectRecipientCountry: 'Select recipient country',
    shipperPostcode: 'Enter shipper postal code',
    recipientPostcode: 'Enter recipient postal code',
    recipientTaxId: 'Enter or select a previous tax ID / VAT number',
    bookingNumber: 'Default placeholder number; editable',
    loadCount: 'Enter package count',
    batteryDetails: 'Multiple selections allowed; leave empty when there is no battery',
    selectFedexDocumentType: 'Select a document type',
    invoiceOptional: 'Optional; leave blank when no invoice is required',
    productType: 'Optional; defaults to Parcel',
    invoiceType: 'Optional; defaults to Commercial Invoice',
    invoiceNumber: 'Defaults to the related shipment request number',
    invoiceFormat: 'Detected automatically after upload',
    importerCountry: 'Optional; use a two-letter code',
    importerCity: 'Select or enter a city',
    subOrderCount: 'Enter 1 to 999 physical pieces'
  },
  hints: {
    dhlInvoiceFormats: 'PDF / PNG / TIFF / GIF / JPEG supported; the format is detected after upload',
    recipientTaxIdHistory: 'FedEx tax IDs are saved for the current user after saving a draft or placing an order.',
    bookingNumberEdit: 'Saving updates the booking number without placing another express order.',

    fedexWeightService: 'Selected product: {service}. eTower validates the weight. If an error occurs, use its message to decide whether to change the product.',
    productSource: 'All consignment products are shown in their original order.',
    productSelection: 'Submit up to 4 selected rows. Quantity and tax-inclusive unit price are required. A blank item description uses the shipment description.',
    productDefaults: 'By default, select the 4 largest original quantities after deduplicating English descriptions, keeping the largest row per description. Order quantity defaults to 1.',
    productEditsOnly: 'Description edits update the deduplicated goods names. FedEx battery descriptions receive the applicable PI prefix. Product edits apply only to express ordering and do not update consignment products.',
    originCountryWriteBack: 'Filled from the consignment and editable. Saved back to the same field after the order is placed successfully.',
    noExclusiveInformation: 'No additional information is required for this carrier. You can place the order now.',
    pickupPending: 'After placing the order, arrange the pickup on the carrier website or with customer service',
    dhlAccountCountry: "The shipper country must match the selected DHL account's registration country.",
    dhlDutyAccountValidation: 'DHL validates the duties and taxes account. Enter a valid account.',
    brokerCountryRequired: 'Select the broker country by name or two-letter code.',
    fedexFileUpload: 'Upload up to 5 files and select a different document type for each file.',
    pieceSummary: 'Total: {weight} kg / {volume} m³'
  },
  contactTable: {
    shipperAddress: 'Shipper Address',
    consigneeAddress: 'Consignee Address'
  },
  productTable: {
    onLabel: 'Send',
    selectRow: 'Send product row {row}',
    sequence: 'No.',
    productName: 'Product Name',
    quantity: 'Quantity',
    unitPriceAll: 'Unit Price (Incl. Tax)',
    cnDesc: 'Chinese Description',
    enDesc: 'English Description'
  },
  table: {
    source: 'Data Source',
    boxQty: 'Cartons',
    totalVolume: 'Total Volume (m³)',
    totalWeight: 'Total Weight (kg)',
    length: 'Length (cm)',
    width: 'Width (cm)',
    height: 'Height (cm)',
    rowNumber: 'No.',
    sku: 'SKU',
    productName: 'Product Name',
    englishName: 'English Product Name',
    exportHsCode: 'Export HS Code',
    importHsCode: 'Import HS Code'
  },
  metricSources: {
    theoretical: 'Theoretical',
    warehouse: 'Warehouse Data'
  },
  pieceTable: {
    referenceNo: 'Piece Reference',
    boxSeq: 'Box Sequence',
    weight: 'Gross Weight (kg)',
    volume: 'Volume (m³)',
    length: 'Length (cm)',
    width: 'Width (cm)',
    height: 'Height (cm)'
  },
  options: {
    carriers: {
      sf: 'SF Express',
      ems: 'EMS',
      fedex: 'FedEx',
      dhl: 'DHL'
    },
    products: {
      sfExpress: 'SF Express',
      sfStandard: 'SF Standard Express',
      sfExpressD: 'Express Class D',
      sfFreight: 'SF Freight',
      sfFreightD: 'SF Freight Class D',
      emsExpress: 'EMS Express',
      fedexPriority: 'FedEx International Priority',
      fedexEconomy: 'FedEx International Economy',
      fedexPriorityFreight: 'FedEx International Priority Freight',
      dhlWorldwide: 'DHL Express Worldwide',
      dhlEconomy: 'DHL Express Economy Select'
    },
    packagingType: {
      yourPackaging: 'Your Packaging',
      fedexEnvelope: 'FedEx Envelope'
    },
    paymentType: {
      sender: 'Sender'
    },
    batteryDetails: {
      lithiumIonPackedWithEquipment: 'Lithium Ion Battery - Packed with Equipment (UN3481.PI966)',
      lithiumIonContainedInEquipment: 'Lithium Ion Battery - Contained in Equipment (UN3481.PI967)',
      lithiumMetalPackedWithEquipment: 'Lithium Metal Battery - Packed with Equipment (UN3091.PI969)',
      lithiumMetalContainedInEquipment: 'Lithium Metal Battery - Contained in Equipment (UN3091.PI970)'
    },
    dutyPayment: {
      sender: 'Sender',
      recipient: 'Recipient',
      thirdParty: 'Third Party',
      collect: 'Collect'
    },
    shipmentPurpose: {
      gift: 'Gift',
      notSold: 'Not Sold',
      personalEffects: 'Personal Effects',
      repairAndReturn: 'Repair and Return',
      sample: 'Sample',
      sold: 'Sold'
    },
    fedexDocumentType: {
      certificateOfOrigin: 'Certificate of Origin',
      commercialInvoice: 'Commercial Invoice',
      etdLabel: 'Shipping Label',
      other: 'Other',
      proFormaInvoice: 'Pro Forma Invoice',
      usmcaCertificationOfOrigin: 'USMCA Certification of Origin',
      usmcaCommercialInvoiceCertificationOfOrigin: 'USMCA Commercial Invoice/Certification of Origin'
    },
    fedexUploadStatus: {
      pending: 'Pending Upload',
      success: 'Uploaded',
      failed: 'Upload Failed'
    },
    dhlInvoice: {
      none: 'No Invoice Required',
      generated: 'DHL Invoice',
      customerUpload: 'Customer Upload'
    },
    dhlProductType: {
      parcel: 'Parcel',
      document: 'Document'
    },
    dhlInvoiceType: {
      commercial: 'Commercial Invoice',
      proforma: 'Proforma Invoice'
    },
    dhlExportReason: {
      permanent: 'Permanent Export',
      temporary: 'Temporary Export',
      return: 'Return',
      exhibitionReturn: 'Used Exhibition Goods to Origin',
      intercompanyUse: 'Intercompany Use',
      commercialPurpose: 'Commercial Purpose or Sale',
      personalUse: 'Personal Belongings or Personal Use',
      sample: 'Sample',
      gift: 'Gift',
      returnToOrigin: 'Return to Origin',
      warrantyReplacement: 'Warranty Replacement',
      diplomaticGoods: 'Diplomatic Goods',
      defenceMaterial: 'Defence Material'
    },
    dhlDangerousType: {
      hd: 'Lithium Ion PI965-966 Section II',
      hv: 'Lithium Ion PI967 Section II',
      hm: 'Lithium Metal PI969 Section II',
      hw: 'Lithium Metal PI970 Section II'
    }
  },
  messages: {
    fieldTooLong: '{field} cannot exceed {max} characters',
    currencyInvalid: 'Use a valid three-letter ISO currency code',
    contactTextInvalid: '{field} must use English or French and cannot be numeric only',
    theoreticalMetricInvalid: 'Theoretical package measurements cannot be negative',
    fedexBrokerCountryInvalid: 'Select a valid broker country',

    productSelectionMax: 'Select up to {max} products. Deselect another row first',
    productSelectionRequired: 'Select 1 to {max} products before ordering',
    productChineseTooLong: 'Product row {row}: Chinese description must not exceed 255 characters',
    productItemsEmpty: 'The consignment has no product details for express ordering',
    productQuantityInvalid: 'Product row {row}: quantity must be an integer from 1 to 2147483647',
    productUnitPriceInvalid: 'Tax-inclusive unit price in row {row} must be greater than 0',
    productEnglishInvalid: 'Use English or French in row {row}; a blank description uses the shipment description. Maximum 60 characters including the FedEx prefix.',
    loadAccountsFailed: 'Failed to load shipping accounts. Please try again later',
    missingOrderForFedexUpload: 'Consignment ID is missing; the FedEx file cannot be uploaded',
    fedexUploadMissingId: 'The FedEx file was uploaded but no file ID was returned',
    fileStored: '{fileName} was stored in File Management',
    fedexUploadFailed: 'Failed to upload the FedEx file. Please try again',
    missingOrderForInvoiceUpload: 'Consignment ID is missing; the invoice cannot be uploaded',
    unsupportedInvoiceFormat: 'Only PDF, PNG, TIFF, GIF, and JPEG invoice files are supported',
    dhlUploadFailed: 'Failed to upload the DHL invoice. Please try again',
    dhlUploadMissingId: 'No file ID was returned for the DHL invoice. Please upload it again',
    dhlUploading: 'The DHL invoice is being uploaded. Please wait',
    incomplete: 'Complete all required express order information',
    selectOriginCountry: 'Select the country of origin',
    originCountryInvalid: 'Select a valid country of origin with a two-letter country code',
    etowerWeightInvalid: 'The weight sent to eTower must be at least 0.000001 kg',
    etowerVolumeInvalid: 'The volume sent to eTower must be at least 0.01 m³',
    etowerDimensionsInvalid: 'The dimensions sent to eTower must be positive; DHL requires length, width and height',
    weightPositiveInteger: 'Total weight must be a positive integer',
    metricPositiveInteger: '{field} must be a positive integer',
    boxQtyInvalid: 'The carton count must be a valid non-negative integer',
    subOrderCountInvalid: 'The piece count must be an integer from 1 to 999 and match the detail rows',
    pieceBoxSeqInvalid: 'The box sequence in piece row {row} must be a positive integer',
    pieceWeightInvalid: 'The weight in piece row {row} must be positive with no more than two decimal places',
    pieceVolumeInvalid: 'The volume in piece row {row} must be positive with no more than two decimal places',
    pieceDhlDimensionsInvalid: 'The length, width, and height in DHL piece row {row} must be positive with no more than two decimal places',
    pieceDimensionsInvalid: 'When provided, the length, width, and height in piece row {row} must be positive with no more than two decimal places',
    stepValidationFailed: 'Page validation failed. Please try again',
    selectShipperCountry: 'Select the shipper country',
    selectRecipientCountry: 'Select the recipient country',
    crossBorderCountrySame: 'FedEx/DHL shipments are cross-border; shipper and recipient countries must differ',
    shipperAddressMissing: 'The selected shipper address is not maintained. Please add it to the contact address information.',
    recipientAddressMissing: 'The selected recipient address is not maintained. Please add it to the contact address information.',
    contactDivisionRequired: 'Enter the {field} for the contact address {party}',
    contactEnglishRequired: 'The {field} of the contact address {party} must not contain Chinese characters',
    selectDutyPayment: 'Select the duties and taxes payer',
    dutyAccountRequired: 'Enter the duties and taxes account',
    selectDhlIncoterm: 'Select the DHL incoterm',
    selectFedexPackagingType: 'Select the FedEx packaging type',
    selectFedexPaymentType: 'Select the FedEx payment type',
    fedexBookingRequired: 'Enter the FedEx booking number',
    fedexLoadCountInvalid: 'Enter a valid FedEx package count',
    fedexUploading: 'The FedEx file is being uploaded. Please wait',
    fedexFileRequired: 'Upload the FedEx file',
    fedexFileCountExceeded: 'You can upload up to {max} FedEx files',
    fedexFileTypeUnsupported: 'FedEx files support BMP, DOC, DOCX, GIF, JPG, PDF, PNG, RTF, TIFF, TXT, XLS, and XLSX only',
    fedexDocumentTypeRequired: 'Select a document type for every FedEx file',
    fedexDocumentTypeDuplicate: 'Select a different document type for each FedEx file',
    fedexPostUploadFailed: 'The express order was placed, but the FedEx files failed to upload to eTower. Check backend logs and do not place the order again.',
    fedexBrokerFieldRequired: 'Enter {field}',
    fedexBrokerCountryMismatch: 'The broker country must match the recipient country',
    dhlInvoiceRequired: 'DHL invoice date and invoice number are required',
    dhlInvoiceFileRequired: 'Upload a valid DHL invoice file; its format is detected automatically',
    dhlImporterCountryInvalid: 'The DHL importer country/region must be a two-letter code',
    sfFieldsLoading: 'SF Express-specific information is still loading. Please try again shortly',
    missingOrderForSubmit: 'Consignment ID is missing; the express order cannot be placed',
    missingOrderForDraft: 'Consignment ID is missing; the draft cannot be saved',
    draftSaved: 'Draft saved',
    draftSaveFailed: 'Failed to save the draft. Please try again',
    draftLoadFailed: 'Failed to load the express order draft. The latest consignment data is used instead',
    orderSuccess: 'Express order placed. Tracking No.: {trackingNo}',
    orderFailed: 'Failed to place the express order. Please try again',
    labelDefaultError: 'Failed to retrieve the shipping label. Check your network and try again',
    labelMissingOrder: 'Consignment ID is missing; the shipping label cannot be retrieved',
    labelStillPending: 'The label is still being retrieved. Reopen the detail page later to check it',
    labelRetryFailed: 'Failed to retrieve the shipping label. Please try again'
  },
  label: {
    title: 'Retrieve Shipping Label',
    loading: 'Retrieving shipping label...',
    success: 'Shipping label retrieved',
    saved: 'Saved to File Management',
    failed: 'Failed to Retrieve Shipping Label',
    retry: 'Retrieve Shipping Label'
  },
  detail: {
    pieceItems: 'Parent-child Shipment Details',
    pieceTrackingNo: 'Piece Tracking Number',
    pieceStatus: 'Order Status',
    productItems: 'Ordered Product Details',
    serviceTitle: 'Express Service',
    sfTitle: 'SF Express-specific Information · Value-added Services',
    dhlTitle: 'DHL-specific Information',
    fedexTitle: 'FedEx-specific Information',
    trackingNo: 'Express Tracking Number',
    service: 'Express Service',
    dimensions: 'Dimensions',
    shipper: 'Shipper Snapshot',
    recipient: 'Recipient Snapshot'
  },
  sf: {
    title: 'SF Express-specific Information',
    valueAddedService: 'Value-added Services',
    helper: 'Select the required value-added services. Fields appear when a service needs parameters.',
    simpleHint: 'Selection Only',
    parameterHint: 'Parameters Required',
    services: {
      cod: 'Cash on Delivery',
      homeDelivery: 'Home Delivery Extension',
      delegatedPickup: 'Delegated Pickup',
      batchDelivery: 'Dispatch When Complete',
      installation: 'Installation Service',
      credential: 'Credential Authentication',
      insured: 'Full Value Insurance',
      timedDelivery: 'Timed Delivery (Specified Time Slot)',
      policyDelivery: 'Policy Delivery',
      selfPickup: 'Service Point Pickup',
      documentDelivery: 'Dedicated Document Delivery',
      traceability: 'Traceability Service',
      packing: 'Packing Service',
      returnTransfer: 'Return Transfer',
      notifiedDelivery: 'Timed Delivery (Await Notification)',
      onTimeGuarantee: 'On-time Guarantee',
      recipientMicroDelivery: 'Recipient Micro-delivery',
      deliveryMicroDelivery: 'Delivery Micro-dispatch',
      packaging: 'Packaging Service',
      electronicReceipt: 'Electronic Return Receipt',
      standardizedPackaging: 'Standardized Packaging Service',
      paperReceipt: 'Paper Return Receipt',
      photoReceipt: 'Photo Return Receipt'
    },
    fields: {
      receiptResourceCode: 'Receipt Resource Code',
      electronicReceiptFile: 'Electronic Receipt File',
      receiptRequirements: 'Receipt Requirements',
      photoCount: 'Number of Photos',
      returnedContent: 'Photo Return Requirements',
      installationType: 'Installation Type',
      credentialType: 'Authentication Method',
      credentialLastSix: 'Last 6 ID Digits',
      insuredAmount: 'Insured Amount',
      deliveryDate: 'Delivery Date',
      deliveryTime: 'Delivery Time Slot',
      codAmount: 'Payment Amount',
      codCard: 'Collection Card Number',
      homeRuleId: 'Home Delivery Rule ID',
      homeRuleName: 'Home Delivery Name',
      serviceQuantity: 'Service Quantity',
      pickupMethod: 'Pickup Method',
      scene: 'Scenario',
      batchNo: 'Batch Number'
    },
    placeholders: {
      resourceCode: 'Enter the resource code',
      selectElectronicReceiptFile: 'Select electronic receipt file',
      credentialLastSix: 'Enter the last 6 digits of the ID number',
      insuredAmount: 'Enter the insured amount',
      codAmount: 'Use origin currency; retain three decimal places',
      codCard: 'Enter the collection card number',
      customerCodeTimestamp: 'Customer code + timestamp'
    },
    hints: {
      credentialPassword: 'SF Express generates the password and sends it to the recipient by SMS.',
      credentialIdCard: 'Enter the last 6 characters of the ID number and inform the recipient yourself.'
    },
    actions: {
      deleteHomeDelivery: 'Delete home delivery item',
      addHomeDelivery: 'Add home delivery item'
    },
    options: {
      integrated: 'Delivery and Installation',
      separate: 'Separate Delivery and Installation',
      confidential: 'Confidential',
      withLetter: 'With Letter',
      confidentialWithLetter: 'Confidential + With Letter',
      password: 'Password',
      idCard: 'ID Card',
      passwordOrIdCard: 'Password + ID Card',
      parentChildScene: 'Parent-child Shipment',
      warehouseScene: 'Multi-warehouse Shipment',
      signature: 'Signature',
      stamp: 'Stamp',
      registerId: 'Record ID Number',
      collectIdCopy: 'Collect ID Copy',
      collectDeliveryStub: 'Collect Delivery Stub (Hong Kong only)',
      otherHongKongFile: 'Other Document (Hong Kong only)',
      receiptDate: 'Receipt Date',
      phone: 'Phone Number',
      idNumber: 'ID Number',
      signatureStamp: 'Signature + Stamp',
      signatureId: 'Signature + ID Number',
      stampId: 'Stamp + ID Number',
      signatureStampId: 'Signature + Stamp + ID Number'
    },
    messages: {
      codAmountInvalid: 'Enter a valid SF Express cash-on-delivery amount',
      codCardRequired: 'Enter the SF Express collection card number',
      homeDeliveryRequired: 'Enter at least one SF Express home delivery service',
      homeDeliveryIncomplete: 'Complete the SF Express home delivery rule ID, name, and positive integer quantity',
      homeDeliveryCommaInvalid: 'SF Express home delivery rule IDs and names cannot contain commas',
      delegatedPickupRequired: 'Select the SF Express delegated pickup type',
      batchSceneRequired: 'Select the SF Express dispatch-when-complete scenario',
      batchNoRequired: 'Enter the SF Express dispatch-when-complete batch number',
      installationRequired: 'Select the SF Express installation service type',
      credentialTypeRequired: 'Select the SF Express authentication type',
      credentialNoInvalid: 'Enter the last 6 digits of the ID number for SF Express authentication',
      insuredAmountInvalid: 'Enter a valid SF Express insured amount',
      timedDeliveryRequired: 'Select the SF Express timed delivery date and time slot',
      electronicReceiptRequired: 'Enter the SF Express electronic receipt resource code',
      electronicReceiptFileRequired: 'Select the SF Express electronic receipt file',
      electronicReceiptFileEmpty: 'The SF Express electronic receipt file cannot be empty',
      electronicReceiptFileTooLarge: 'The SF Express electronic receipt file cannot exceed 20 MB',
      electronicReceiptFileTypeInvalid: 'SF Express electronic receipts support pdf, doc, docx, xls, xlsx, ppt, pptx, jpg, jpeg, and png files only',
      electronicReceiptFileNameInvalid: 'The SF Express electronic receipt file name cannot contain / \\ : * ? " < > | or emoji',
      electronicReceiptFileChanged: 'The SF Express electronic receipt file changed; confirm the order again',
      electronicReceiptUploadFailed: 'The SF Express electronic receipt upload failed because no resource code was returned',
      paperReceiptRequired: 'Select at least one SF Express paper receipt requirement',
      photoCountInvalid: 'Enter the number of SF Express return photos',
      photoTypeRequired: 'Select at least one SF Express photo return requirement',
      photoTypeInvalid: 'Select valid SF Express photo return requirements'
    }
  }
}
